import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import type { PropsWithChildren } from 'react';
import { apiService, CLIENT_TYPE_HEADER, CLIENT_TYPE_WEB } from '@/api/services/api.service';
import type {
  AuthorizedImageContextValue,
  AuthorizedImageLoadPriority,
} from './AuthorizedImage.types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const MAX_PARALLEL_IMAGE_FETCHES = 3;

type ImageRequestStatus = 'queued' | 'active' | 'loaded';

interface QueueEntry {
  cancelled: boolean;
  priority: AuthorizedImageLoadPriority;
  reject: (reason?: unknown) => void;
  run: () => void;
}

interface ImageCacheEntry {
  abortController: AbortController;
  cancelQueued?: () => void;
  consumers: Set<symbol>;
  promoteQueued?: () => void;
  promise: Promise<string>;
  status: ImageRequestStatus;
}

const buildAbsoluteUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const createAbortError = () =>
  new DOMException('Image request cancelled', 'AbortError');

export const AuthorizedImageContext =
  createContext<AuthorizedImageContextValue | null>(null);

interface AuthorizedImageProviderProps {
  /**
   * Token JWT używany do pobrania chronionych obrazów. Jeśli pominięty,
   * Provider sięga po token z `apiService.getAuthToken()`.
   * Przekaż jawnie token z `?token=` query (quick-access), żeby nie
   * polegać na `sessionStorage`.
   */
  token?: string;
}

export const AuthorizedImageProvider = ({
  token,
  children,
}: PropsWithChildren<AuthorizedImageProviderProps>) => {
  // Cache trzymany w refach, żeby zmiana tokenu nie inwalidowała wpisów
  // tworzonych dla bieżącego raportu, ale zarazem żeby unmount całego
  // providera zwolnił WSZYSTKIE obiektowe URL-e.
  const cacheRef = useRef<Map<string, ImageCacheEntry>>(new Map());
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const tokenRef = useRef<string | undefined>(token);
  const activeRequestsRef = useRef(0);
  const queuedRequestsRef = useRef<QueueEntry[]>([]);
  const isMountedRef = useRef(true);

  // Token aktualizujemy przez ref, żeby `load` nie dostawał nowej referencji
  // przy każdym renderze providera (zachowuje stabilność dla konsumentów).
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    isMountedRef.current = true;

    const objectUrls = objectUrlsRef.current;
    const cache = cacheRef.current;
    const queuedRequests = queuedRequestsRef.current;
    return () => {
      isMountedRef.current = false;
      queuedRequests.forEach((entry) => {
        entry.cancelled = true;
        entry.reject(createAbortError());
      });
      cache.forEach((entry) => {
        entry.abortController.abort();
      });
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.clear();
      cache.clear();
      queuedRequests.length = 0;
    };
  }, []);

  const runNextQueuedRequest = useCallback(() => {
    while (queuedRequestsRef.current.length > 0) {
      const next = queuedRequestsRef.current.shift();
      if (!next || next.cancelled) {
        continue;
      }
      next.run();
      return;
    }
  }, []);

  const queueRequest = useCallback((queueEntry: QueueEntry) => {
    const queue = queuedRequestsRef.current;

    if (queueEntry.priority !== 'high') {
      queue.push(queueEntry);
      return;
    }

    const firstNormalPriorityIndex = queue.findIndex(
      (entry) => entry.priority !== 'high',
    );

    if (firstNormalPriorityIndex === -1) {
      queue.push(queueEntry);
      return;
    }

    queue.splice(firstNormalPriorityIndex, 0, queueEntry);
  }, []);

  const promoteQueuedRequest = useCallback(
    (queueEntry: QueueEntry) => {
      if (queueEntry.cancelled || queueEntry.priority === 'high') {
        return;
      }

      const queue = queuedRequestsRef.current;
      const currentIndex = queue.indexOf(queueEntry);

      if (currentIndex === -1) {
        return;
      }

      queue.splice(currentIndex, 1);
      queueEntry.priority = 'high';
      queueRequest(queueEntry);
    },
    [queueRequest],
  );

  const runInBatch = useCallback(
    <T,>(
      task: () => Promise<T>,
      onStart?: () => void,
      priority: AuthorizedImageLoadPriority = 'normal',
    ): {
      cancelQueued: () => void;
      isStarted: () => boolean;
      promoteQueued: () => void;
      promise: Promise<T>;
    } => {
      let queueEntry: QueueEntry | null = null;
      let started = false;

      const promise = new Promise<T>((resolve, reject) => {
        const run = () => {
          if (queueEntry?.cancelled) {
            reject(createAbortError());
            return;
          }

          started = true;
          onStart?.();
          activeRequestsRef.current += 1;

          Promise.resolve()
            .then(task)
            .then(resolve, reject)
            .finally(() => {
              activeRequestsRef.current -= 1;
              runNextQueuedRequest();
            });
        };

        queueEntry = { cancelled: false, priority, reject, run };

        if (activeRequestsRef.current < MAX_PARALLEL_IMAGE_FETCHES) {
          run();
          return;
        }

        queueRequest(queueEntry);
      });

      const cancelQueued = () => {
        if (!queueEntry || started || queueEntry.cancelled) {
          return;
        }

        queueEntry.cancelled = true;
        queueEntry.reject(createAbortError());
      };

      const promoteQueued = () => {
        if (!queueEntry || started || queueEntry.cancelled) {
          return;
        }

        promoteQueuedRequest(queueEntry);
      };

      return { cancelQueued, isStarted: () => started, promoteQueued, promise };
    },
    [promoteQueuedRequest, queueRequest, runNextQueuedRequest],
  );

  const releaseConsumer = useCallback(
    (imageUrl: string, consumerId: symbol) => {
      const entry = cacheRef.current.get(imageUrl);
      if (!entry) {
        return;
      }

      entry.consumers.delete(consumerId);

      if (entry.consumers.size > 0 || entry.status === 'loaded') {
        return;
      }

      if (entry.status === 'queued') {
        entry.cancelQueued?.();
      } else {
        entry.abortController.abort();
      }

      cacheRef.current.delete(imageUrl);
    },
    [],
  );

  const load = useCallback(
    (
      imageUrl: string,
      options?: {
        priority?: AuthorizedImageLoadPriority;
        signal?: AbortSignal;
      },
    ): Promise<string> => {
      let entry = cacheRef.current.get(imageUrl);
      const priority = options?.priority ?? 'normal';

      if (entry?.status === 'queued' && priority === 'high') {
        entry.promoteQueued?.();
      }

      if (!entry) {
        const abortController = new AbortController();
        const authToken = tokenRef.current ?? apiService.getAuthToken();
        const batchedRequest = runInBatch(
          () =>
            fetch(buildAbsoluteUrl(imageUrl), {
              headers: {
                [CLIENT_TYPE_HEADER]: CLIENT_TYPE_WEB,
                ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
              },
              signal: abortController.signal,
            })
              .then(async (response) => {
                if (!response.ok) {
                  throw new Error(`Image fetch failed (${response.status})`);
                }
                return response.blob();
              })
              .then((blob) => {
                if (!isMountedRef.current || abortController.signal.aborted) {
                  throw createAbortError();
                }

                const objectUrl = URL.createObjectURL(blob);
                objectUrlsRef.current.add(objectUrl);
                return objectUrl;
              }),
          () => {
            const currentEntry = cacheRef.current.get(imageUrl);
            if (currentEntry) {
              currentEntry.status = 'active';
            }
          },
          priority,
        );

        const promise = batchedRequest.promise
          .then((objectUrl) => {
            const currentEntry = cacheRef.current.get(imageUrl);
            if (currentEntry) {
              currentEntry.status = 'loaded';
              currentEntry.consumers.clear();
            }
            return objectUrl;
          })
          .catch((error) => {
            if (cacheRef.current.get(imageUrl) === entry) {
              cacheRef.current.delete(imageUrl);
            }
            throw error;
          });

        entry = {
          abortController,
          cancelQueued: batchedRequest.cancelQueued,
          consumers: new Set<symbol>(),
          promoteQueued: batchedRequest.promoteQueued,
          promise,
          status: batchedRequest.isStarted() ? 'active' : 'queued',
        };

        cacheRef.current.set(imageUrl, entry);
      }

      const consumerId = Symbol(imageUrl);
      entry.consumers.add(consumerId);

      const release = () => releaseConsumer(imageUrl, consumerId);
      const signal = options?.signal;

      if (!signal) {
        return entry.promise;
      }

      if (signal.aborted) {
        release();
        return Promise.reject(createAbortError());
      }

      return new Promise<string>((resolve, reject) => {
        const handleAbort = () => {
          release();
          reject(createAbortError());
        };

        signal.addEventListener('abort', handleAbort, { once: true });

        entry.promise.then(resolve, reject).finally(() => {
          signal.removeEventListener('abort', handleAbort);
        });
      });
    },
    [releaseConsumer, runInBatch],
  );

  const value = useMemo<AuthorizedImageContextValue>(() => ({ load }), [load]);

  return (
    <AuthorizedImageContext.Provider value={value}>
      {children}
    </AuthorizedImageContext.Provider>
  );
};
